// Organisation — Overview & onboarding checklist (Spec §8.5 / §10.6, Figure 20/53).
// A single guided starting point for org admins: Seats · Access · SSO · Keys,
// each backed by the same endpoints their own screens use, with a completion
// state and a NextStepBanner pointing at the next incomplete step.

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Users, FolderTree, ShieldCheck, KeyRound } from 'lucide-react'
import {
  getLicencePool,
  getSeats,
  getIdentityConfig,
  getKeyConfig,
  getKeyHealth,
  getProjects,
  getAccessMatrix,
} from '../../services/api'
import { useActiveOrg } from '../../hooks/useActiveOrg'
import AdminGate from '../../components/organisation/AdminGate'
import { httpStatusOf } from '../../components/organisation/gateStatus'
import PageHeader from '../../components/ui/PageHeader'
import NextStepBanner, { NextStepArrow } from '../../components/ui/NextStepBanner'
import WorkflowStepper, { type WorkflowStep } from '../../components/ui/WorkflowStepper'
import HelpPopover from '../../components/help/HelpPopover'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'

// ---------------------------------------------------------------------------
// Step metadata — order matches the spec: Seats · Access · SSO · Keys.
// ---------------------------------------------------------------------------

type StepKey = 'seats' | 'access' | 'sso' | 'keys'

interface StepMeta {
  label: string
  route: string
  helpKey: string
  icon: LucideIcon
  /** shown on the NextStepBanner when this is the next incomplete step */
  nextStepMessage: string
}

// NOTE: helpContent.ts is off-limits for this change, so the PageHeader below
// points at the 'org-overview' key that config/navigation.ts already reserves
// for this screen's nav entry (added by a sibling session in this redesign
// wave) — HelpPopover degrades gracefully today and lights up automatically
// once that article lands. Each checklist step below instead reuses the real,
// already-existing article for its own destination screen (org-members /
// org-access / org-identity / org-keys).
const STEP_META: Record<StepKey, StepMeta> = {
  seats: {
    label: 'Seats',
    route: '/org/members',
    helpKey: 'org-members',
    icon: Users,
    nextStepMessage: 'No members invited yet. Invite your team and allocate seats so they can sign in.',
  },
  access: {
    label: 'Access',
    route: '/org/access',
    helpKey: 'org-access',
    icon: FolderTree,
    nextStepMessage: 'No project or folder access has been granted yet. Give members a way in.',
  },
  sso: {
    label: 'SSO',
    route: '/org/identity',
    helpKey: 'org-identity',
    icon: ShieldCheck,
    nextStepMessage: 'SSO not configured — set it up so members can sign in with your identity provider.',
  },
  keys: {
    label: 'Keys',
    route: '/org/keys',
    helpKey: 'org-keys',
    icon: KeyRound,
    nextStepMessage: 'Your encryption key needs attention before it can be relied on.',
  },
}

const STEP_ORDER: StepKey[] = ['seats', 'access', 'sso', 'keys']

// A grant whose source is one of these is the acting admin's own default
// standing (or a deny), not evidence that access has actually been
// configured for anyone — so it never counts toward the Access step.
const ADMIN_LIKE_SOURCES = new Set(['owner', 'org_admin', 'platform_admin', 'default_deny', 'deny'])

type StepState = 'loading' | 'done' | 'todo' | 'error'

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OrgOverview() {
  const navigate = useNavigate()

  const { orgId, orgName, isOrgAdmin, isPlatformAdmin, isPersonalOrg } = useActiveOrg()

  const enabled = Boolean(isOrgAdmin && orgId)

  // ---- Queries (hooks must run unconditionally) --------------------------
  const poolQuery = useQuery({
    queryKey: ['licence-pool', orgId],
    queryFn: () => getLicencePool(orgId as string),
    enabled,
  })
  const seatsQuery = useQuery({
    queryKey: ['seats', orgId],
    queryFn: () => getSeats(orgId as string),
    enabled,
  })
  const identityQuery = useQuery({
    queryKey: ['identity-config', orgId],
    queryFn: () => getIdentityConfig(orgId as string),
    enabled,
  })
  const keyConfigQuery = useQuery({
    queryKey: ['org-key-config', orgId],
    queryFn: () => getKeyConfig(orgId as string),
    enabled,
  })
  const keyHealthQuery = useQuery({
    queryKey: ['org-key-health', orgId],
    queryFn: () => getKeyHealth(orgId as string),
    enabled,
  })
  const projectsQuery = useQuery({
    queryKey: ['projects-for-org-overview', orgId],
    queryFn: getProjects,
    enabled,
  })

  // Access matrix needs the org's own project ids first; cap at 8 columns,
  // the same limit ProjectFolderAccess enforces for matrix readability.
  const projectIds = useMemo(
    () => (projectsQuery.data ?? []).slice(0, 8).map((p) => p.project_id),
    [projectsQuery.data],
  )
  const matrixQuery = useQuery({
    queryKey: ['access-matrix-org-overview', orgId, projectIds],
    queryFn: () => getAccessMatrix(orgId as string, projectIds),
    enabled: enabled && projectIds.length > 0,
  })

  // ---- Access gate ---------------------------------------------------------
  if (!isOrgAdmin || !orgId) {
    return (
      <AdminGate
        area="the organisation overview"
        orgName={isPersonalOrg ? undefined : orgName}
        isPlatformAdmin={isPlatformAdmin}
      />
    )
  }

  // A 403 from any admin endpoint means the caller's role does not cover this
  // organisation: explain that once, instead of four "could not load" rows.
  const forbidden = [poolQuery, seatsQuery, identityQuery, keyConfigQuery].find(
    (q) => q.isError && httpStatusOf(q.error) === 403,
  )
  if (forbidden) {
    return <AdminGate area="the organisation overview" orgName={orgName} error={forbidden.error} />
  }

  // ---- Derived: Seats ------------------------------------------------------
  const visibleSeats = (seatsQuery.data ?? []).filter((s) => s.state !== 'removed')
  const seatsDone = visibleSeats.length > 0
  const seatsState: StepState = poolQuery.isLoading || seatsQuery.isLoading
    ? 'loading'
    : poolQuery.isError || seatsQuery.isError
      ? 'error'
      : seatsDone
        ? 'done'
        : 'todo'
  const seatsHint = seatsState === 'loading'
    ? 'Checking…'
    : seatsState === 'error'
      ? 'Could not load seat data — try again shortly.'
      : seatsDone
        ? `${visibleSeats.length} member${visibleSeats.length === 1 ? '' : 's'} on seats`
        : 'No members invited yet'

  // ---- Derived: Access ------------------------------------------------------
  const accessRows = matrixQuery.data?.rows ?? []
  const accessGrantedCount = accessRows.filter((row) =>
    Object.values(row.cells).some(
      (cell) => cell.allowed && !!cell.level && !ADMIN_LIKE_SOURCES.has(cell.source),
    ),
  ).length
  const accessDone = accessGrantedCount > 0
  const accessState: StepState = projectsQuery.isLoading
    ? 'loading'
    : projectsQuery.isError
      ? 'error'
      : projectIds.length === 0
        ? 'todo'
        : matrixQuery.isLoading
          ? 'loading'
          : matrixQuery.isError
            ? 'error'
            : accessDone
              ? 'done'
              : 'todo'
  const accessHint = accessState === 'loading'
    ? 'Checking…'
    : accessState === 'error'
      ? 'Could not load access data — try again shortly.'
      : projectIds.length === 0
        ? 'No projects yet — nothing to grant access to'
        : accessDone
          ? `${accessGrantedCount} member${accessGrantedCount === 1 ? '' : 's'} with explicit access`
          : 'No project or folder access granted yet'

  // ---- Derived: SSO ---------------------------------------------------------
  const identityCfg = identityQuery.data
  const ssoDone = !!identityCfg?.idp_type && (identityCfg?.verified_domains?.length ?? 0) > 0
  // Identity & SSO tells the truth about federation: only status "live" means members can
  // actually sign in through the IdP. A saved, verified config is "Configured, not live".
  const ssoLive = ssoDone && identityCfg?.status === 'live'
  const ssoState: StepState = identityQuery.isLoading
    ? 'loading'
    : identityQuery.isError
      ? 'error'
      : ssoDone
        ? 'done'
        : 'todo'
  const ssoHint = ssoState === 'loading'
    ? 'Checking…'
    : ssoState === 'error'
      ? 'Could not load identity data — try again shortly.'
      : ssoLive
        ? `Connected via ${identityCfg?.idp_type?.toUpperCase()}`
        : ssoDone
          ? `Configured, not live — ${identityCfg?.idp_type?.toUpperCase()} is saved but sign-in isn't switched on yet`
          : 'Members sign in with a password — no identity provider connected'

  // ---- Derived: Keys ----------------------------------------------------------
  // Platform-managed custody needs zero setup, so this step is "done" as soon
  // as the config loaded and health hasn't reported a real problem — it never
  // fakes a green tick while the key itself is degraded.
  const keyHealthy = (keyHealthQuery.data?.healthy ?? keyHealthQuery.data?.valid) !== false
  const keysDone = !!keyConfigQuery.data && keyHealthy
  const keysState: StepState = keyConfigQuery.isLoading || keyHealthQuery.isLoading
    ? 'loading'
    : keyConfigQuery.isError
      ? 'error'
      : keysDone
        ? 'done'
        : 'todo'
  const custodyLabel = keyConfigQuery.data?.custody_mode === 'byok' ? 'your own key (BYOK)' : 'platform-managed key'
  const keysHint = keysState === 'loading'
    ? 'Checking…'
    : keysState === 'error'
      ? 'Could not load key configuration — try again shortly.'
      : keysDone
        ? `Healthy · ${custodyLabel}`
        : `Degraded · ${custodyLabel} needs attention`

  // ---- Assemble the checklist -----------------------------------------------
  const stepData: Record<StepKey, { state: StepState; hint: string }> = {
    seats: { state: seatsState, hint: seatsHint },
    access: { state: accessState, hint: accessHint },
    sso: { state: ssoState, hint: ssoHint },
    keys: { state: keysState, hint: keysHint },
  }

  const completedCount = STEP_ORDER.filter((k) => stepData[k].state === 'done').length
  const anyLoading = STEP_ORDER.some((k) => stepData[k].state === 'loading')
  const nextIncomplete = STEP_ORDER.find((k) => stepData[k].state === 'todo')

  const workflowSteps: WorkflowStep[] = STEP_ORDER.map((key) => {
    const meta = STEP_META[key]
    const { state, hint } = stepData[key]
    return {
      key,
      label: meta.label,
      hint,
      status:
        state === 'done'
          ? 'completed'
          : key === nextIncomplete
            ? 'active'
            : 'pending',
      onSelect: () => navigate(meta.route),
    }
  })

  const allDone = !anyLoading && completedCount === STEP_ORDER.length

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${orgName || 'Your organisation'} — Overview`}
        description="A guided starting point for setting up your organisation — seats, access, identity and encryption, all in one place."
        helpKey="org-overview"
        status={
          anyLoading
            ? undefined
            : {
                label: `${completedCount} of ${STEP_ORDER.length} complete`,
                tone: allDone ? 'success' : 'warning',
              }
        }
        banner={
          anyLoading ? undefined : allDone ? (
            <NextStepBanner
              tone="success"
              message="Your organisation is fully set up — seats, access, identity and encryption are all configured."
            />
          ) : nextIncomplete ? (
            <NextStepBanner
              message={STEP_META[nextIncomplete].nextStepMessage}
              action={
                <button
                  type="button"
                  onClick={() => navigate(STEP_META[nextIncomplete].route)}
                  className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  <NextStepArrow>Go to {STEP_META[nextIncomplete].label}</NextStepArrow>
                </button>
              }
            />
          ) : undefined
        }
      />

      {anyLoading ? (
        <div className="light-card p-4 lg:p-6">
          <LoadingSkeleton rows={4} type="list" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              icon={Users}
              label="Seats"
              value={seatsState === 'error' ? 'Unavailable' : `${visibleSeats.length}`}
              caption={
                seatsState === 'error'
                  ? 'could not load seats'
                  : poolQuery.data
                  ? `of ${poolQuery.data.seats_purchased_full + poolQuery.data.seats_purchased_contributor} purchased`
                  : 'allocated'
              }
              helpKey="org-members"
            />
            <SummaryCard
              icon={FolderTree}
              label="Access"
              value={accessState === 'error' ? 'Unavailable' : `${accessGrantedCount}`}
              caption={accessState === 'error' ? 'could not load access' : 'members with explicit grants'}
              helpKey="org-access"
            />
            <SummaryCard
              icon={ShieldCheck}
              label="SSO"
              value={
                ssoState === 'error'
                  ? 'Unavailable'
                  : ssoLive
                    ? 'Connected'
                    : ssoDone
                      ? 'Configured, not live'
                      : 'Not connected'
              }
              caption={
                ssoState === 'error'
                  ? 'could not load identity'
                  : identityCfg?.idp_type
                    ? identityCfg.idp_type.toUpperCase()
                    : 'no identity provider'
              }
              helpKey="org-identity"
            />
            <SummaryCard
              icon={KeyRound}
              label="Keys"
              value={keysState === 'error' || keyHealthQuery.isError ? 'Unavailable' : keysDone ? 'Healthy' : 'Degraded'}
              caption={keysState === 'error' ? 'could not load keys' : custodyLabel}
              helpKey="org-keys"
            />
          </div>

          {/* Onboarding checklist */}
          <div className="light-card p-4 lg:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-800">Onboarding checklist</h2>
              <span className="text-xs text-gray-500">
                {completedCount} of {STEP_ORDER.length} steps complete
              </span>
            </div>
            <WorkflowStepper
              steps={workflowSteps}
              orientation="vertical"
              aria-label="Organisation onboarding checklist"
            />
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary stat card
// ---------------------------------------------------------------------------

function SummaryCard({
  icon: Icon,
  label,
  value,
  caption,
  helpKey,
}: {
  icon: LucideIcon
  label: string
  value: string
  caption: string
  helpKey: string
}) {
  return (
    <div className="light-card p-4 lg:p-6">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-iflow-100 text-iflow-700 flex items-center justify-center flex-shrink-0">
          <Icon size={16} />
        </span>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <HelpPopover topic={helpKey} size={14} />
      </div>
      <p className="text-2xl font-bold text-gray-800 mt-2 truncate" title={value}>
        {value}
      </p>
      <p className="text-xs text-gray-500 mt-1 truncate" title={caption}>
        {caption}
      </p>
    </div>
  )
}
